import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router';
import { INTERVAL } from '@voladeum/client';
import { useClientContext } from 'client/context';
import { setCandleValidations } from 'ducks/candle-validations/slices';
import { setCandleWorkers } from 'ducks/candle-workers/slices';
import { setCandles } from 'ducks/candles/slices';
import { setPair } from 'ducks/pair/slices';
import { updateSettings } from 'ducks/settings/slices';
import { useAppDispatch } from 'store/hooks';
import { unixConversion } from 'utils/dates';
import { parseQuery } from 'utils/queries';

import { PairQuery } from './types';

const usePairApi = () => {
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const { pair_id = '' } = useParams();
  const { Voladeum, handleError } = useClientContext();

  const getData = useCallback(async () => {
    try {
      if (!pair_id) return;
      dispatch(updateSettings({ loading: true, loading_component: true }));
      const { since_milliseconds, to_milliseconds, interval_id } = parseQuery<PairQuery>(search);

      const [voladeum_pair, voladeum_candle_workers, voladeum_validations, voladeum_candles] =
        await Promise.all([
          Voladeum.pairs.fetch(pair_id),
          Voladeum.pairs.candleWorkers(pair_id),
          Voladeum.pairs.candleValidations(pair_id),
          Voladeum.candles.list(pair_id, {
            interval_id: interval_id || INTERVAL.MINUTE,
            since_milliseconds: unixConversion(since_milliseconds),
            to_milliseconds: unixConversion(to_milliseconds),
          }),
        ]);

      dispatch(setPair(voladeum_pair.data));
      dispatch(setCandleValidations(voladeum_validations.data));
      dispatch(setCandleWorkers(voladeum_candle_workers.data));
      dispatch(setCandles(voladeum_candles.data));
      dispatch(updateSettings({ loading: false, loading_component: false }));
    } catch (err) {
      handleError(err);
    }
  }, [dispatch, handleError, pair_id, search, Voladeum]);

  return { getData };
};

export { usePairApi };
